import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  type CustomerTask,
  type CustomerTaskFilter,
  customerColumns,
  customerSections,
  customerTasksQueryOptions,
  filterCustomerTasks,
} from '@/entities/task'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { DueDate } from '@/shared/ui/due-date'
import { EmptyState } from '@/shared/ui/empty-state'
import { FilterTab } from '@/shared/ui/filter-tab'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { PriorityChip } from '@/shared/ui/priority-chip'
import { Spinner } from '@/shared/ui/spinner'

interface MyTasksSearch {
  // Дефолты (таб «Всё», вид «список») живут вне URL — параметр появляется только
  // у выбранного вручную.
  filter?: CustomerTaskFilter
  view?: 'board'
  project?: string
}

export const Route = createFileRoute('/my/tasks')({
  validateSearch: (s: Record<string, unknown>): MyTasksSearch => ({
    ...(s.filter === 'in-work' ||
    s.filter === 'waiting' ||
    s.filter === 'overdue' ||
    s.filter === 'done'
      ? { filter: s.filter }
      : {}),
    ...(s.view === 'board' ? { view: 'board' as const } : {}),
    ...(typeof s.project === 'string' && s.project !== '' ? { project: s.project } : {}),
  }),
  head: () => ({ meta: [{ title: 'Мои задачи · STIT Task' }] }),
  component: MyTasksPage,
})

const FILTERS: { value: CustomerTaskFilter; label: string }[] = [
  { value: 'all', label: 'Всё' },
  { value: 'in-work', label: 'В работе' },
  { value: 'waiting', label: 'Ждёт ответа' },
  { value: 'done', label: 'Выполненные' },
  { value: 'overdue', label: 'Просрочено' },
]

// Витрина заказчика: только те задачи, которые команда открыла его компании
// (фильтрует бэкенд по customer-сессии, 00-architecture § 4). Ни создания, ни
// правки, ни обсуждения — клиент здесь читатель, а не участник рабочего процесса.
function MyTasksPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const tasksQ = useQuery(customerTasksQueryOptions)
  const filter = search.filter ?? 'all'

  const all = tasksQ.data ?? []
  const projects = [...new Set(all.map((t) => t.projectName).filter((n): n is string => !!n))]
  const scoped = search.project ? all.filter((t) => t.projectName === search.project) : all
  const tasks = filterCustomerTasks(scoped, filter)

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader
        title="Мои задачи"
        description="Задачи, которые команда открыла вашей компании. Список пополняется по мере работы."
        actions={
          <div className="flex items-center gap-2">
            <ViewTab
              active={search.view !== 'board'}
              onClick={() => void navigate({ search: withSearch(search, { view: undefined }) })}
              icon="my-tasks"
            >
              Список
            </ViewTab>
            <ViewTab
              active={search.view === 'board'}
              onClick={() => void navigate({ search: withSearch(search, { view: 'board' }) })}
              icon="kanban"
            >
              Доска
            </ViewTab>
          </div>
        }
      />

      {all.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-5 border-b border-border-subtle pb-px">
          {FILTERS.map((f) => (
            <FilterTab
              key={f.value}
              active={filter === f.value}
              onClick={() => void navigate({ search: withSearch(search, { filter: f.value }) })}
            >
              {f.label}
            </FilterTab>
          ))}
        </div>
      )}

      {projects.length > 1 && (
        <nav aria-label="Фильтр по проектам" className="mt-4 flex flex-wrap items-center gap-2">
          <ProjectChip
            active={!search.project}
            onClick={() => void navigate({ search: withSearch(search, { project: undefined }) })}
          >
            Все проекты
          </ProjectChip>
          {projects.map((name) => (
            <ProjectChip
              key={name}
              active={search.project === name}
              onClick={() => void navigate({ search: withSearch(search, { project: name }) })}
            >
              {name}
            </ProjectChip>
          ))}
        </nav>
      )}

      <div className="mt-8">
        {tasksQ.isPending ? (
          <Spinner label="Загрузка задач" />
        ) : tasksQ.isError ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-text-secondary">Не удалось загрузить задачи.</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void tasksQ.refetch()
              }}
            >
              Повторить
            </Button>
          </div>
        ) : all.length === 0 ? (
          <EmptyState
            title="Задач пока нет"
            description="Здесь появятся задачи вашего проекта, как только команда откроет их вам."
          />
        ) : tasks.length === 0 ? (
          <p className="rounded-card bg-bg-surface px-6 py-10 text-center text-sm text-text-tertiary">
            Нет задач под этот фильтр.
          </p>
        ) : search.view === 'board' ? (
          <CustomerBoard tasks={tasks} />
        ) : (
          <CustomerTaskList tasks={tasks} />
        )}
      </div>
    </div>
  )
}

// Пересобираем весь search целиком (exactOptional запрещает undefined-как-удаление):
// дефолтные значения просто не попадают в объект → чистый URL.
type SearchPatch = { [K in keyof MyTasksSearch]?: MyTasksSearch[K] | undefined }

function withSearch(search: MyTasksSearch, patch: SearchPatch): MyTasksSearch {
  const next = { ...search, ...patch }
  return {
    ...(next.filter && next.filter !== 'all' ? { filter: next.filter } : {}),
    ...(next.view ? { view: next.view } : {}),
    ...(next.project ? { project: next.project } : {}),
  }
}

function ViewTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: 'my-tasks' | 'kanban'
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-control border px-3 py-1.5 font-mono text-xs uppercase tracking-wide transition-colors',
        active
          ? 'border-border-strong bg-bg-elevated text-text-primary'
          : 'border-border-default text-text-secondary hover:text-text-primary',
      )}
    >
      <Icon name={icon} size={14} />
      {children}
    </button>
  )
}

function ProjectChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-pill border px-3 py-1 text-xs transition-colors',
        active
          ? 'border-border-strong bg-bg-elevated text-text-primary'
          : 'border-border-default text-text-secondary hover:text-text-primary',
      )}
    >
      {children}
    </button>
  )
}

// Список — тот же обход по срочности, что у команды: срочное сверху, ожидание и
// закрытое внизу. Таймеров у заказчика нет, поэтому нет и секции «Сейчас в работе».
function CustomerTaskList({ tasks }: { tasks: CustomerTask[] }) {
  const sections = customerSections(tasks)
  return (
    <div className="flex flex-col gap-10">
      {sections.map((section) => (
        <section key={section.id} aria-label={section.title}>
          <div className="flex items-center gap-3">
            <h2 className="label shrink-0">{section.title}</h2>
            <span aria-hidden className="h-px flex-1 bg-border-subtle" />
            <span data-numeric className="mono-num shrink-0 text-xs text-text-tertiary">
              {section.tasks.length}
            </span>
          </div>
          <ul className="mt-4 flex flex-col gap-3">
            {section.tasks.map((task) => (
              <li key={task.id}>
                <CustomerTaskRow task={task} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

// Строка витрины: контекст назван словами (проект · доска · этап) — клиент не
// знает наших идентификаторов и досок наизусть.
function CustomerTaskRow({ task }: { task: CustomerTask }) {
  return (
    <Link
      to="/my/tasks/$taskId"
      params={{ taskId: task.id }}
      className="group flex items-center gap-4 rounded-control bg-bg-surface p-4 ring-1 ring-border-subtle ring-inset transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {task.type === 'bug' && (
            <span
              role="img"
              aria-label="Баг"
              title="Баг"
              className="inline-flex shrink-0 text-text-tertiary"
            >
              <Icon name="bug" size={14} />
            </span>
          )}
          <h3 className="truncate text-sm text-text-primary">{task.title}</h3>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-tertiary">
          {task.priority !== null && <PriorityChip priority={task.priority} />}
          {task.projectName && <span>{task.projectName}</span>}
          {task.boardName && (
            <>
              <span aria-hidden>·</span>
              <span>{task.boardName}</span>
            </>
          )}
          {task.columnName && (
            <>
              <span aria-hidden>·</span>
              <span className={task.done ? 'text-success' : undefined}>{task.columnName}</span>
            </>
          )}
          {task.dueDate && (
            <>
              <span aria-hidden>·</span>
              {/* Задача в done-колонке закрыта: срок остаётся, тревога — нет. */}
              <DueDate dueDate={task.dueDate} muted={task.done} />
            </>
          )}
        </div>
      </div>
      <Icon
        name="chevron-right"
        size={16}
        className="shrink-0 text-text-tertiary transition-colors group-hover:text-text-secondary"
      />
    </Link>
  )
}

// Доска витрины: те же колонки, что у команды, — иначе «этап» у клиента и у нас
// назывался бы по-разному. Перетаскивания нет: клиент ничего не меняет.
function CustomerBoard({ tasks }: { tasks: CustomerTask[] }) {
  const columns = customerColumns(tasks)
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {columns.map((column) => (
        <section key={column.id} className="flex w-[86vw] shrink-0 flex-col gap-3 sm:w-[300px]">
          <div className="flex items-center gap-2 px-1">
            <h2 className="text-sm font-medium text-text-primary">{column.name}</h2>
            <span data-numeric className="mono-num text-xs text-text-tertiary">
              {column.tasks.length}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {column.tasks.map((task) => (
              <Link
                key={task.id}
                to="/my/tasks/$taskId"
                params={{ taskId: task.id }}
                className="rounded-control bg-bg-surface p-3 ring-1 ring-border-subtle ring-inset transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
              >
                <h3 className="line-clamp-2 text-sm text-text-primary">{task.title}</h3>
                {(task.priority !== null || task.dueDate) && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {task.priority !== null && <PriorityChip priority={task.priority} />}
                    {/* Задача в done-колонке закрыта: срок остаётся, тревога — нет. */}
                    {task.dueDate && <DueDate dueDate={task.dueDate} muted={task.done} />}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
